import './styles/main.css';
import { phishingModel } from './ml/model.js';

// Initialize app
class PhishGuardApp {
  constructor() {
    this.isModelReady = false;
    this.init();
  }

  async init() {
    this.render();
    this.bindEvents();
    await this.trainModel();
  }

  render() {
    const app = document.getElementById('app');
    app.innerHTML = `
      <div class="bg-gradient"></div>
      <div class="container">
        <header class="header">
          <div class="logo">
            <div class="logo-icon">🛡️</div>
            <span class="logo-text">PhishGuard AI</span>
          </div>
          <p class="tagline">
            Advanced phishing detection powered by <span class="highlight">Random Forest</span> machine learning. 
            Analyze URLs instantly and protect yourself from malicious websites.
          </p>
        </header>

        <div class="main-card">
          <div class="input-section">
            <label class="input-label">Enter URL to Analyze</label>
            <div class="input-wrapper">
              <input 
                type="text" 
                class="url-input" 
                id="urlInput"
                placeholder="https://example.com/login"
                autocomplete="off"
              />
              <button class="analyze-btn" id="analyzeBtn" disabled>
                <span class="btn-icon">🔍</span>
                <span>Analyze</span>
              </button>
            </div>
          </div>

          <div class="results-section" id="resultsSection">
            <!-- Results will be injected here -->
          </div>
        </div>

        <div class="stats-section" id="statsSection">
          <div class="stat-card">
            <div class="stat-icon">🎯</div>
            <div class="stat-value" id="accuracyStat">--</div>
            <div class="stat-label">Model Accuracy</div>
          </div>
          <div class="stat-card">
            <div class="stat-icon">🌲</div>
            <div class="stat-value">50</div>
            <div class="stat-label">Decision Trees</div>
          </div>
          <div class="stat-card">
            <div class="stat-icon">📊</div>
            <div class="stat-value">29</div>
            <div class="stat-label">URL Features</div>
          </div>
          <div class="stat-card">
            <div class="stat-icon">⚡</div>
            <div class="stat-value"><50ms</div>
            <div class="stat-label">Analysis Time</div>
          </div>
        </div>

        <div class="how-it-works">
          <h2>How It Works</h2>
          <div class="steps-grid">
            <div class="step">
              <div class="step-number">1</div>
              <h3>Feature Extraction</h3>
              <p>We extract 29 unique features from the URL including length, special characters, domain patterns, and suspicious keywords.</p>
            </div>
            <div class="step">
              <div class="step-number">2</div>
              <h3>Random Forest Analysis</h3>
              <p>Our ensemble of 50 decision trees analyzes the features, each voting on whether the URL is legitimate or phishing.</p>
            </div>
            <div class="step">
              <div class="step-number">3</div>
              <h3>Confidence Scoring</h3>
              <p>The model aggregates all votes to produce a confidence score and detailed breakdown of risk factors.</p>
            </div>
            <div class="step">
              <div class="step-number">4</div>
              <h3>Instant Results</h3>
              <p>Get immediate feedback with actionable insights about the URL's safety and specific risk indicators.</p>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  bindEvents() {
    const analyzeBtn = document.getElementById('analyzeBtn');
    const urlInput = document.getElementById('urlInput');

    analyzeBtn.addEventListener('click', () => this.analyzeUrl());
    
    urlInput.addEventListener('keypress', (e) => {
      if (e.key === 'Enter' && this.isModelReady) {
        this.analyzeUrl();
      }
    });

    urlInput.addEventListener('input', () => {
      const resultsSection = document.getElementById('resultsSection');
      resultsSection.classList.remove('visible');
    });
  }

  async trainModel() {
    const analyzeBtn = document.getElementById('analyzeBtn');
    analyzeBtn.innerHTML = `
      <div class="loading-spinner"></div>
      <span>Training Model...</span>
    `;

    try {
      const metrics = await phishingModel.train({
        nEstimators: 50,
        maxDepth: 10
      });

      document.getElementById('accuracyStat').textContent = `${metrics.testAccuracy}%`;
      
      this.isModelReady = true;
      analyzeBtn.disabled = false;
      analyzeBtn.innerHTML = `
        <span class="btn-icon">🔍</span>
        <span>Analyze</span>
      `;

      console.log('Model ready!', metrics);
    } catch (error) {
      console.error('Training failed:', error);
      analyzeBtn.innerHTML = `
        <span class="btn-icon">❌</span>
        <span>Training Failed</span>
      `;
    }
  }

  analyzeUrl() {
    const urlInput = document.getElementById('urlInput');
    const url = urlInput.value.trim();

    if (!url) {
      this.showError('Please enter a URL to analyze');
      return;
    }

    try {
      const result = phishingModel.predict(url);
      this.displayResults(result);
    } catch (error) {
      console.error('Analysis error:', error);
      this.showError('Invalid URL. Please enter a valid URL.');
    }
  }

  displayResults(result) {
    const resultsSection = document.getElementById('resultsSection');
    
    const classificationClass = result.classification;
    const icon = result.classification === 'safe' ? '✓' : result.classification === 'phishing' ? '✗' : '⚠';
    const title = result.classification === 'safe' ? 'URL Appears Safe' : 
                  result.classification === 'phishing' ? 'Phishing Detected!' : 'Suspicious URL';
    const description = result.classification === 'safe' ? 
      'This URL does not show typical phishing characteristics.' :
      result.classification === 'phishing' ?
      'This URL exhibits multiple phishing indicators. Do not enter any personal information.' :
      'This URL shows some suspicious patterns. Proceed with caution.';

    const confidenceClass = result.classification;
    const confidenceValue = result.classification === 'safe' ? 
      100 - result.probability : result.probability;

    resultsSection.innerHTML = `
      <div class="result-header ${classificationClass}">
        <div class="result-icon">${icon}</div>
        <div class="result-content">
          <h2>${title}</h2>
          <p>${description}</p>
        </div>
      </div>

      <div class="confidence-section">
        <div class="confidence-label">
          <span>Risk Level</span>
          <span class="confidence-value" style="color: var(--${classificationClass === 'safe' ? 'success' : classificationClass === 'phishing' ? 'error' : 'warning'})">${result.probability}%</span>
        </div>
        <div class="confidence-bar">
          <div class="confidence-fill ${confidenceClass}" style="width: ${result.probability}%"></div>
        </div>
      </div>

      <div class="features-section">
        <h3>Analysis Details</h3>
        <div class="features-grid">
          ${result.featureDescriptions.map(feature => `
            <div class="feature-card">
              <div class="feature-indicator ${feature.risk}"></div>
              <div class="feature-info">
                <h4>${feature.name}</h4>
                <p>${feature.value}</p>
              </div>
            </div>
          `).join('')}
        </div>
      </div>
    `;

    resultsSection.classList.add('visible');
    resultsSection.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  showError(message) {
    const resultsSection = document.getElementById('resultsSection');
    resultsSection.innerHTML = `
      <div class="result-header phishing">
        <div class="result-icon">!</div>
        <div class="result-content">
          <h2>Error</h2>
          <p>${message}</p>
        </div>
      </div>
    `;
    resultsSection.classList.add('visible');
  }
}

// Initialize app
new PhishGuardApp();
